import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DollarSign } from 'lucide-react';

interface CoachEarningsTableProps {
  coachId?: string;
  clubId?: string;
}

export const CoachEarningsTable: React.FC<CoachEarningsTableProps> = ({
  coachId,
  clubId
}) => {
  const { data: earnings, isLoading } = useQuery({
    queryKey: ['coach-earnings', coachId, clubId],
    queryFn: async () => {
      let query = supabase
        .from('coach_earnings')
        .select(`
          *,
          payment_transactions(stripe_payment_intent_id)
        `)
        .order('payment_date', { ascending: false });

      if (coachId) {
        query = query.eq('coach_id', coachId);
      }
      if (clubId) {
        query = query.eq('club_id', clubId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return <div>Loading earnings...</div>;
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'paid': return 'default';
      case 'pending': return 'secondary';
      case 'failed': return 'destructive';
      default: return 'outline';
    }
  };

  const getEarningsTypeLabel = (type: string) => {
    switch (type) {
      case 'salary': return 'Salary';
      case 'clinic': return 'Clinic';
      case 'bonus': return 'Bonus';
      default: return type;
    }
  };

  const totalEarnings = earnings?.reduce((sum, earning) => sum + earning.net_amount, 0) || 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Coach Earnings
            </CardTitle>
            <CardDescription>
              Track payments and earnings for coaches
            </CardDescription>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">
              ${(totalEarnings / 100).toFixed(2)}
            </div>
            <div className="text-sm text-muted-foreground">
              Total Earnings
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {earnings && earnings.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Gross Amount</TableHead>
                <TableHead>Net Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {earnings.map((earning) => (
                <TableRow key={earning.id}>
                  <TableCell>
                    {new Date(earning.payment_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {getEarningsTypeLabel(earning.earnings_type)}
                  </TableCell>
                  <TableCell>
                    ${(earning.gross_amount / 100).toFixed(2)}
                  </TableCell>
                  <TableCell className="font-medium">
                    ${(earning.net_amount / 100).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(earning.payment_status)}>
                      {earning.payment_status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No earnings records found.
          </div>
        )}
      </CardContent>
    </Card>
  );
};